import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  where?: [string, ...string[]];
  whereNote?: string;
}[] = [
  {
    title: 'What the CSM workload shows',
    body: 'The CSM workload page shows how your clients are spread over customer-success managers (CSMs), and which clients have no manager at all. It counts clients only: a lead or a prospect has no manager and is not on the page. A client that has never had a stage set counts as a client, as it does everywhere else in Revvly. The page is for people who lead customer success. It reads the manager and the customer-success status you keep on each client, and it changes nothing until you rebalance.',
  },
  {
    title: 'Who can open it',
    body: 'You need the CRM feature on your workspace and the Customer-success team lead role. Workspace admins and owners hold that role automatically, so they can always open the page. Everyone else is not shown the button, and a direct link to the page is refused with a message that only customer-success team leads and workspace admins can see the workload. Customer-success manager is a separate role. It lets someone see the client fields limited to customer success, and it does not open the workload page.',
    where: ['Org Settings', 'Roles', 'Customer-success team lead', 'Grant'],
    whereNote: 'Admins and owners only. Granting the role takes effect the next time the person loads the page.',
  },
  {
    title: 'Open the page',
    body: 'The page is not in the sidebar. Open Clients and choose the CSM workload button in the toolbar above the list. It opens the page at /clients/workload, and the browser tab is titled CSM workload.',
    where: ['Clients', 'CSM workload'],
    whereNote:
      'If you do not see the button, you are missing the team lead role or the CRM feature. See who can open it above.',
  },
  {
    title: 'Give every client a manager and a status',
    body: 'The page is only as good as the two fields it reads. The customer-success manager is the person looking after the client, and it is separate from the account owner. The customer-success status is one of Trial, Onboarding, Nurturing, Renewal or Churned, and it is separate from the sales stage, which stays Client. Both are optional. Set them when you add a client, on the Edit client form, or for many clients at once with Edit selected on the Clients list. A manager does not have to be an active member: someone who has left stays listed, marked inactive, so the clients they still look after are not hidden.',
    where: ['Clients', 'a client', 'Edit client'],
    whereNote: 'The status can be cleared. A client with no status is counted as No status.',
  },
  {
    title: 'Read the four numbers at the top',
    body: 'Clients is every client on the page. Without a manager is how many have no customer-success manager; it turns green when that is zero and orange when it is not. Trial and onboarding is the clients whose status is Trial or Onboarding, which is your newest accounts. In renewal is the clients whose status is Renewal. All four follow the Hide churned switch, which is on when the page opens, so churned clients are left out of every count, bar and list until you turn it off.',
  },
  {
    title: 'Read the book by CSM',
    body: 'Each row is one manager and their book of clients. Managers are listed busiest first and the unassigned row is always last, so a gap in coverage is the last thing on the screen. The number is how many clients the manager has. The bar is the size of that book against the largest one on the page, split into coloured segments by status: Trial, Onboarding, Nurturing, Renewal, and Churned when you show it. The text beside the bar spells out the same split, so you do not rely on colour. A manager whose whole book is churned drops off the page while churned clients are hidden. A manager who is no longer in the workspace appears as Former team member until their clients are moved.',
  },
  {
    title: 'See one manager’s clients',
    body: 'Choose the arrow at the end of a manager’s row. A panel opens with their status split and the clients by name, each with its status. A client name opens that client. The panel names the first 100 clients by name; if the book is bigger, it says so, and the rest are still counted. Choose Move clients in the panel footer to start a rebalance from that manager’s book.',
  },
  {
    title: 'Work through the unassigned queue',
    body: 'The Unassigned queue beside the table lists clients nobody manages, five at a time, each with its status and an Assign button. View all opens the full list in the same panel. When every client has a manager the queue says so. On the table, the unassigned row has an Assign N button that opens the rebalance dialog with all of them ticked, which is the fastest way to give a new batch of clients a manager.',
    where: ['Clients', 'CSM workload', 'Unassigned queue', 'Assign'],
  },
  {
    title: 'Rebalance clients',
    body: 'Choose Rebalance at the top of the page, or Assign or Move clients from a row. Pick the book to move From (a manager, or Unassigned) and the manager to move To. Each name shows how many clients they have now. Tick the clients to move, or use the status filter to narrow the list and the tick at the top to select everything shown. The Effect panel shows each book before and after and how many clients are moving, by status, before you commit. Choose Move N clients to apply it. One call moves them all; a client that cannot be changed is reported by name with a reason, and the rest are still moved.',
    where: ['Clients', 'CSM workload', 'Rebalance'],
    whereNote:
      'The dialog lists the first 100 clients of a book. If a book is bigger, move those, then open the page again for the rest.',
  },
  {
    title: 'What a rebalance changes, and what it leaves alone',
    body: 'Only the customer-success manager changes. The account owner, the customer-success status, subscriptions and invoices stay exactly as they were. Each client is checked the same way a single edit is, so you cannot move a client you could not edit by hand. The page refreshes when the move finishes, and the new split is what you see.',
  },
];

export function CsmWorkloadGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Balance client work across CSMs — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">CSM workload</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Balance <em>client work</em> across your CSMs.
        </h1>
        <p className="dp-oneliner">
          See how many clients each customer-success manager looks after, which clients have no
          manager, and move accounts from one book to another. You need the customer-success team
          lead role, or to be a workspace admin or owner. For the fields behind it, see{' '}
          <Link to="/docs/guides/custom-fields">Add custom fields and tags</Link>; for roles, see{' '}
          <Link to="/docs/guides/teams-and-seats">Set up teams, seats and invites</Link>.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
        </div>
      ))}
    </div>
  );
}
